import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchTransferFleetVehicles,
  transferRecordQueryKeys,
  updateTransferDraft,
  type TransferDraftLineAdd,
  type TransferRecord,
  type TransferRecordLine,
} from '@/data/transferRecord';
import type { TransferItemSearchResult } from '@/data/transferCreate';
import TransferItemSearch from '../../../components/transfers/TransferItemSearch';
import TransferRecordDialog from './TransferRecordDialog';
import { lineDisplayName, linePhysicalGuard, lineSku, lineTrackingNote, siteName } from './transferRecordView';
import type { TransferActionRunner } from './useTransferRecord';

type LineMode = 'catalog' | 'external' | 'accessory' | 'vehicle';

const MODES: ReadonlyArray<{ key: LineMode; label: string }> = [
  { key: 'catalog', label: 'Catalog item' },
  { key: 'external', label: 'Not in the catalog' },
  { key: 'accessory', label: 'Accessory' },
  { key: 'vehicle', label: 'Vehicle' },
];

type TransferLineDialogProps = {
  record: TransferRecord;
  /** The line to edit; null adds one. */
  line: TransferRecordLine | null;
  runner: TransferActionRunner;
  onClose: () => void;
  onDone: (message: string) => void;
};

function wholeNumber(value: string): number | null {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
}

/** Add a line (catalog search, not in the catalog, accessory or vehicle), or change one's quantity and notes. */
export default function TransferLineDialog({ record, line, runner, onClose, onDone }: TransferLineDialogProps) {
  return line
    ? <EditLineDialog record={record} line={line} runner={runner} onClose={onClose} onDone={onDone} />
    : <AddLineDialog record={record} runner={runner} onClose={onClose} onDone={onDone} />;
}

function AddLineDialog({ record, runner, onClose, onDone }: Omit<TransferLineDialogProps, 'line'>) {
  const { transfer } = record;
  const fromSiteId = transfer.fromSite?.id ?? null;
  const [mode, setMode] = React.useState<LineMode>('catalog');
  const [picked, setPicked] = React.useState<TransferItemSearchResult | null>(null);
  const [name, setName] = React.useState('');
  const [code, setCode] = React.useState('');
  const [vehicleId, setVehicleId] = React.useState('');
  const [qty, setQty] = React.useState('1');
  const [notes, setNotes] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const nameId = React.useId();
  const codeId = React.useId();
  const vehicleFieldId = React.useId();
  const qtyId = React.useId();
  const notesId = React.useId();

  const vehiclesQuery = useQuery({
    queryKey: transferRecordQueryKeys.vehicles(fromSiteId ?? ''),
    queryFn: () => fetchTransferFleetVehicles(fromSiteId ?? ''),
    enabled: mode === 'vehicle' && Boolean(fromSiteId),
    staleTime: 60_000,
  });
  const vehicles = vehiclesQuery.data ?? [];

  const searchWindow = React.useMemo(
    () => ({ shipAt: transfer.expectedShipAt, returnAt: transfer.expectedReturnAt }),
    [transfer.expectedReturnAt, transfer.expectedShipAt],
  );
  const singleUnit = mode === 'vehicle' || Boolean(picked?.asset);
  const searchRef = React.useRef<HTMLInputElement>(null);

  // The search field takes focus when it shows (on open, and after "Change").
  React.useEffect(() => {
    if (mode === 'catalog' && !picked) searchRef.current?.focus({ preventScroll: true });
  }, [mode, picked]);

  const handlePick = React.useCallback((result: TransferItemSearchResult) => {
    setPicked(result);
    setQty(String(!result.asset && result.suggestedQty && result.suggestedQty > 0 ? result.suggestedQty : 1));
    setError(null);
  }, []);

  function chooseMode(next: LineMode) {
    setMode(next);
    setError(null);
  }

  function clearPick() {
    setPicked(null);
  }

  function handleNameChange(event: React.ChangeEvent<HTMLInputElement>) {
    setName(event.target.value);
  }

  function handleCodeChange(event: React.ChangeEvent<HTMLInputElement>) {
    setCode(event.target.value);
  }

  function handleVehicleChange(event: React.ChangeEvent<HTMLSelectElement>) {
    setVehicleId(event.target.value);
  }

  function handleQtyChange(event: React.ChangeEvent<HTMLInputElement>) {
    setQty(event.target.value);
  }

  function handleNotesChange(event: React.ChangeEvent<HTMLInputElement>) {
    setNotes(event.target.value);
  }

  function buildLine(): TransferDraftLineAdd | string {
    const count = singleUnit ? 1 : wholeNumber(qty);
    if (count == null) return 'Enter a whole number of units, 1 or more.';
    const note = notes.trim() || null;
    if (mode === 'catalog') {
      if (!picked) return 'Search for the item to add, then choose it.';
      if (picked.asset) {
        if (!picked.itemId) return 'That unit has no catalog item, so it cannot be added here.';
        return { item_id: picked.itemId, qty: 1, asset_ids: [picked.asset.id], notes: note };
      }
      if (picked.itemId) return { item_id: picked.itemId, qty: count, notes: note };
      if (picked.productId) return { product_id: picked.productId, qty: count, notes: note };
      return 'That match cannot be added. Choose another.';
    }
    if (mode === 'vehicle') {
      if (!vehicleId) return 'Choose the vehicle.';
      return { vehicle_id: vehicleId, qty: 1, notes: note };
    }
    const label = name.trim();
    if (!label) return mode === 'accessory' ? 'Name the accessory.' : 'Name the item.';
    return {
      external_name: label,
      external_code: code.trim() || null,
      qty: count,
      accessory: mode === 'accessory',
      notes: note,
    };
  }

  async function handleSubmit() {
    const built = buildLine();
    if (typeof built === 'string') {
      setError(built);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await runner.run(
        'line-add',
        built,
        (requestKey) => updateTransferDraft(transfer.id, requestKey, { lines: { add: [built] } }),
        { movesStock: true, fallback: 'The line was not added.' },
      );
      onDone('Line added');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The line was not added.');
    } finally {
      setSaving(false);
    }
  }

  const vehicleStatus = !fromSiteId
    ? 'The transfer has no shipping site.'
    : vehiclesQuery.isLoading
      ? 'Loading vehicles…'
      : vehiclesQuery.isError
        ? 'Vehicles could not be loaded. Try again.'
        : !vehicles.length ? `No active vehicles at ${siteName(transfer.fromSite, 'the shipping site')}.` : null;

  return (
    <TransferRecordDialog
      title="Add a line"
      eyebrow={transfer.docNo ?? 'Transfer'}
      subtitle={`From ${siteName(transfer.fromSite, 'the shipping site')} to ${siteName(transfer.toSite, 'the receiving site')}`}
      onClose={onClose}
      busy={saving}
      error={error}
      onSubmit={handleSubmit}
      size="lg"
      footer={(
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Adding…' : 'Add line'}
        </button>
      )}
    >
      <div className="transfer-record-segments" role="group" aria-label="What to add">
        {MODES.map((option) => (
          <button
            key={option.key}
            type="button"
            className="transfer-record-segments__option"
            aria-pressed={mode === option.key}
            onClick={() => chooseMode(option.key)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {mode === 'catalog' ? (
        picked ? (
          <div className="transfer-record-picked">
            <div className="transfer-record-picked__main">
              <strong>{picked.asset ? picked.asset.serial ?? picked.asset.barcode ?? picked.name : picked.name}</strong>
              <small>
                {[picked.asset ? picked.name : null, picked.sku, picked.category].filter(Boolean).join(' · ')}
                {picked.freeAtFrom != null && !picked.asset ? ` · ${picked.freeAtFrom.toLocaleString()} free at ${siteName(transfer.fromSite, 'the shipping site')}` : ''}
              </small>
            </div>
            <button type="button" className="btn xs" onClick={clearPick}>Change</button>
          </div>
        ) : (
          <div className="transfer-record-field">
            <span className="transfer-record-field__label">Find the item</span>
            <TransferItemSearch
              fromLocationId={fromSiteId}
              toLocationId={transfer.toSite?.id ?? null}
              fromSiteName={transfer.fromSite?.name ?? null}
              window={searchWindow}
              eventId={record.events[0]?.id ?? null}
              onPick={handlePick}
              placeholder="Name, SKU or serial"
              inputRef={searchRef}
            />
          </div>
        )
      ) : null}

      {mode === 'external' || mode === 'accessory' ? (
        <div className="transfer-record-form-row transfer-record-form-row--name">
          <div className="transfer-record-field">
            <label className="transfer-record-field__label" htmlFor={nameId}>{mode === 'accessory' ? 'Accessory' : 'Item name'}</label>
            <input
              id={nameId}
              className="transfer-record-field__control"
              value={name}
              onChange={handleNameChange}
              placeholder={mode === 'accessory' ? 'e.g. Power cables for the washes' : 'e.g. Rental hazer from Stage Supply'}
              data-autofocus="true"
              maxLength={200}
            />
          </div>
          <div className="transfer-record-field">
            <label className="transfer-record-field__label" htmlFor={codeId}>Code</label>
            <input
              id={codeId}
              className="transfer-record-field__control"
              value={code}
              onChange={handleCodeChange}
              placeholder="Optional"
              autoComplete="off"
              spellCheck={false}
              maxLength={80}
            />
          </div>
        </div>
      ) : null}

      {mode === 'vehicle' ? (
        <div className="transfer-record-field">
          <label className="transfer-record-field__label" htmlFor={vehicleFieldId}>Vehicle</label>
          <select
            id={vehicleFieldId}
            className="transfer-record-field__control"
            value={vehicleId}
            onChange={handleVehicleChange}
            disabled={!vehicles.length}
            data-autofocus="true"
          >
            <option value="">Choose a vehicle</option>
            {vehicles.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>
                {[vehicle.name, vehicle.type, vehicle.plate].filter(Boolean).join(' · ')}
              </option>
            ))}
          </select>
          {vehicleStatus ? <p className="transfer-record-field__hint" role="status">{vehicleStatus}</p> : null}
        </div>
      ) : null}

      <div className="transfer-record-form-row">
        <div className="transfer-record-field transfer-record-field--narrow">
          <label className="transfer-record-field__label" htmlFor={qtyId}>Quantity</label>
          <input
            id={qtyId}
            className="transfer-record-field__control"
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={singleUnit ? '1' : qty}
            onChange={handleQtyChange}
            disabled={singleUnit}
            aria-describedby={singleUnit ? `${qtyId}-note` : undefined}
          />
        </div>
        <div className="transfer-record-field">
          <label className="transfer-record-field__label" htmlFor={notesId}>Notes</label>
          <input
            id={notesId}
            className="transfer-record-field__control"
            value={notes}
            onChange={handleNotesChange}
            placeholder="Optional"
            maxLength={500}
          />
        </div>
      </div>
      {singleUnit ? (
        <p id={`${qtyId}-note`} className="transfer-record-field__hint">
          {mode === 'vehicle' ? 'A vehicle line always moves one vehicle.' : 'A line for one named unit always moves one.'}
        </p>
      ) : null}
    </TransferRecordDialog>
  );
}

function EditLineDialog({ record, line, runner, onClose, onDone }: TransferLineDialogProps & { line: TransferRecordLine }) {
  const guard = React.useMemo(() => linePhysicalGuard(line), [line]);
  const fixedQty = line.kind === 'asset' || line.kind === 'vehicle';
  const [qty, setQty] = React.useState(String(line.qty));
  const [notes, setNotes] = React.useState(line.notes ?? '');
  const [confirmRemove, setConfirmRemove] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const qtyId = React.useId();
  const notesId = React.useId();
  const minimum = guard.locked ? Math.max(1, guard.protectedQuantity) : 1;

  function handleQtyChange(event: React.ChangeEvent<HTMLInputElement>) {
    setQty(event.target.value);
  }

  function handleNotesChange(event: React.ChangeEvent<HTMLInputElement>) {
    setNotes(event.target.value);
  }

  async function save() {
    const update: { line_id: string; qty?: number; notes?: string | null } = { line_id: line.id };
    if (!fixedQty) {
      const count = wholeNumber(qty);
      if (count == null) {
        setError('Enter a whole number of units, 1 or more.');
        return;
      }
      if (count < minimum) {
        setError(guard.message ?? `This line cannot go below ${minimum}.`);
        return;
      }
      if (count !== line.qty) update.qty = count;
    }
    const nextNotes = notes.trim() || null;
    if (nextNotes !== (line.notes ?? null)) update.notes = nextNotes;
    if (update.qty == null && !('notes' in update)) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await runner.run(
        `line-update:${line.id}`,
        update,
        (requestKey) => updateTransferDraft(record.transfer.id, requestKey, { lines: { update: [update] } }),
        { movesStock: update.qty != null, fallback: 'The line was not changed.' },
      );
      onDone('Line saved');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The line was not changed.');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!confirmRemove) {
      setConfirmRemove(true);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await runner.run(
        `line-remove:${line.id}`,
        line.id,
        (requestKey) => updateTransferDraft(record.transfer.id, requestKey, { lines: { remove: [line.id] } }),
        { movesStock: true, fallback: 'The line was not removed.' },
      );
      onDone('Line removed');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The line was not removed.');
      setConfirmRemove(false);
    } finally {
      setSaving(false);
    }
  }

  function handleRemoveClick() {
    void remove();
  }

  const sku = lineSku(line);
  return (
    <TransferRecordDialog
      title={lineDisplayName(line)}
      eyebrow="Edit line"
      subtitle={[sku, lineTrackingNote(line)].filter(Boolean).join(' · ')}
      onClose={onClose}
      busy={saving}
      error={error}
      onSubmit={() => { void save(); }}
      footer={(
        <>
          <button
            type="button"
            className="btn danger transfer-record-dialog__remove"
            onClick={handleRemoveClick}
            disabled={saving || guard.locked}
            title={guard.locked ? guard.message ?? undefined : undefined}
          >
            {confirmRemove ? 'Remove this line?' : 'Remove line'}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
      <div className="transfer-record-form-row">
        <div className="transfer-record-field transfer-record-field--narrow">
          <label className="transfer-record-field__label" htmlFor={qtyId}>Quantity</label>
          <input
            id={qtyId}
            className="transfer-record-field__control"
            type="number"
            inputMode="numeric"
            min={minimum}
            step={1}
            value={fixedQty ? '1' : qty}
            onChange={handleQtyChange}
            disabled={fixedQty}
            data-autofocus="true"
          />
        </div>
        <div className="transfer-record-field">
          <label className="transfer-record-field__label" htmlFor={notesId}>Notes</label>
          <input
            id={notesId}
            className="transfer-record-field__control"
            value={notes}
            onChange={handleNotesChange}
            placeholder="Optional"
            maxLength={500}
          />
        </div>
      </div>
      {fixedQty ? (
        <p className="transfer-record-field__hint">
          {line.kind === 'vehicle' ? 'A vehicle line always moves one vehicle.' : 'A line for one named unit always moves one.'}
        </p>
      ) : null}
      {guard.locked ? <p className="transfer-record-field__hint" role="note">{guard.message}</p> : null}
      <p className="transfer-record-field__hint">To move a different item, remove this line and add the right one.</p>
    </TransferRecordDialog>
  );
}
